import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { CreateSixModalInformation, CreateSixState, CreateSixStep, LocalDraft } from '@/types/create-six.types';
import { createSixThunk } from '@/store/thunks/create-six/createSixThunk';
import { updateSixThunk } from '@/store/thunks/create-six/updateSixThunk';
import { validateSixNameThunk } from '@/store/thunks/create-six/validateSixThunk';
import { extractThunkError } from '@/store/utils';
import { fetchCreateSixModalInformationThunk } from '@/store/thunks/create-six/fetchCreateSixModalInformation';
import { MIN_LEN } from '@/constants';

const FIRST_STEP: CreateSixStep = 'Basics';

const initialState: CreateSixState = {
  draft: {
    ownerIds: [],
    internalParticipantIds: [],
    externalParticipantIds: [],
    newExternalUsers: [],
    name: '',
    description: '',
  },
  step: FIRST_STEP,
  submitting: false,
  hasPendingExternalManualEntries: false,
  fieldErrors: {},
  internalUsers: [],
  invitedUser: null,
  externalUsers: [],
  loadingInternal: false,
  loadingExternal: false,
  showSuccessScreen: false,
};

export const createSixSlice = createSlice({
  name: 'createSix',
  initialState,
  reducers: {
    setDraftField<K extends keyof LocalDraft>(state: CreateSixState, action: PayloadAction<{ key: K; value: LocalDraft[K] }>) {
      state.draft[action.payload.key] = action.payload.value;
    },
    setStep(state, action: PayloadAction<CreateSixStep>) {
      state.step = action.payload;
    },
    setHasPendingExternalManualEntries(state, action: PayloadAction<boolean>) {
      state.hasPendingExternalManualEntries = action.payload;
    },
    resetCreateSixState() {
      return initialState;
    },
    setEditSixId(state, action: PayloadAction<string | undefined>) {
      state.editSixId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCreateSixModalInformationThunk.pending, (state) => {
        state.loadingInternal = true;
        state.loadingExternal = true;
        state.screenError = undefined;
      })
      .addCase(fetchCreateSixModalInformationThunk.fulfilled, (state, action: PayloadAction<CreateSixModalInformation>) => {
        state.loadingInternal = false;
        state.loadingExternal = false;
        state.internalUsers = action.payload.internalUsers;
        state.externalUsers = action.payload.externalUsers;
      })
      .addCase(fetchCreateSixModalInformationThunk.rejected, (state, action) => {
        state.loadingInternal = false;
        state.loadingExternal = false;
        state.screenError = extractThunkError(action.payload).message ?? action.error.message;
      })
      .addCase(validateSixNameThunk.pending, (state) => {
        state.submitting = true;
        state.fieldErrors = {};
        state.screenError = undefined;
      })
      .addCase(validateSixNameThunk.fulfilled, (state) => {
        state.submitting = false;
      })
      .addCase(validateSixNameThunk.rejected, (state, action) => {
        state.submitting = false;
        const { fieldErrors, message } = extractThunkError(action.payload);
        if (Object.keys(fieldErrors).length > MIN_LEN) {
          state.fieldErrors = fieldErrors;
        } else {
          state.screenError = message ?? action.error.message;
        }
      })
      .addCase(createSixThunk.pending, (state) => {
        state.submitting = true;
        state.fieldErrors = {};
        state.screenError = undefined;
      })
      .addCase(createSixThunk.fulfilled, (state, action) => {
        state.submitting = false;
        state.created = action.payload;
      })
      .addCase(createSixThunk.rejected, (state, action) => {
        state.submitting = false;
        const { fieldErrors, message } = extractThunkError(action.payload);
        if (Object.keys(fieldErrors).length > MIN_LEN) {
          state.fieldErrors = fieldErrors;
        } else {
          state.screenError = message ?? action.error.message;
        }
      })
      .addCase(updateSixThunk.pending, (state) => {
        state.submitting = true;
        state.fieldErrors = {};
        state.screenError = undefined;
      })
      .addCase(updateSixThunk.fulfilled, (state) => {
        state.submitting = false;
      })
      .addCase(updateSixThunk.rejected, (state, action) => {
        state.submitting = false;
        const { fieldErrors, message } = extractThunkError(action.payload);
        if (Object.keys(fieldErrors).length > MIN_LEN) {
          state.fieldErrors = fieldErrors;
        } else {
          state.screenError = message ?? action.error.message;
        }
      });
  },
});

const { actions } = createSixSlice;

export const {
  setDraftField,
  setStep,
  setHasPendingExternalManualEntries,
  resetCreateSixState,
  setEditSixId,
} = actions;
export default createSixSlice.reducer;
